import {
  Component,
  ViewEncapsulation,
  Inject,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatChipsModule } from "@angular/material/chips";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { trigger, style, transition, animate } from "@angular/animations";
import {
  selectTicketCategories,
  selectTicketStatuses,
  selectTicketCategoriesLoading,
  selectTicketStatusesLoading,
  selectTickets,
  selectDownloadingAttachment,
  selectPreviewingAttachment,
} from "@store/selectors/issue-tracker/issue-tracker.selectors";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import {
  Ticket,
  TicketComment,
  TicketCategory,
  TicketStatus,
} from "@models/ticket-data.model";
import { selectOrganizationName } from "@store/selectors/add-organization/add-organization.selectors";
import {
  selectImpactedMeasureOptions,
  ImpactedMeasureOption,
} from "@store/selectors/dashboard/dashboard.selectors";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { Actions, ofType } from "@ngrx/effects";
import { selectOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { Organization } from "@models/organization.model";
import { RoleConstants, ROLE_GROUPS } from "../../../constants/roles";
import { SupportTicketConstants } from "../../../constants/support-ticket";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import {
  AddTicketAttachmentModalComponent,
  TicketAttachmentResult,
} from "../add-ticket-attachment-modal/add-ticket-attachment-modal.component";
import { ErrorDialogService } from "@services/error-dialog.service";

export interface TicketDetailsDialogData {
  ticketId: number;
  ticketData: Ticket;
}

@Component({
  selector: "app-ticket-details-modal",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatSnackBarModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: "./ticket-details-modal.component.html",
  styleUrl: "./ticket-details-modal.component.css",
  animations: [
    trigger("slideDown", [
      transition(":enter", [
        style({ height: "0", opacity: 0, overflow: "hidden" }),
        animate("300ms ease-in-out", style({ height: "*", opacity: 1 })),
      ]),
      transition(":leave", [
        style({ height: "*", opacity: 1 }),
        animate(
          "300ms ease-in-out",
          style({ height: "0", opacity: 0, overflow: "hidden" })
        ),
      ]),
    ]),
  ],
})
export class TicketDetailsModalComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroy$ = new Subject<void>();

  @ViewChild("commentsContainer") private commentsContainer!: ElementRef;

  private ticketSubject = new BehaviorSubject<Ticket>(this.data.ticketData);
  ticketDetails$ = this.ticketSubject.asObservable();

  ticketComments$ = this.ticketDetails$.pipe(
    map((ticket) => ticket.ticketComments || [])
  );

  categories: TicketCategory[] = [];
  statuses: TicketStatus[] = [];
  organizations: Organization[] = [];
  userRoles: string[] = [];
  impactedMeasureOptions: ImpactedMeasureOption[] = [];

  categoriesLoading$ = this.store.select(selectTicketCategoriesLoading);
  statusesLoading$ = this.store.select(selectTicketStatusesLoading);
  previewingAttachment$ = this.store.select(selectPreviewingAttachment);
  downloadingAttachment$ = this.store.select(selectDownloadingAttachment);
  orgName$ = this.store.select(selectOrganizationName);
  organizations$ = this.store.select(selectOrgs);
  activeUser$ = this.store.select(selectActiveUser);

  isEditMode = true;
  isUpdating = false;
  isAddingComment = false;
  showComments = false;
  private shouldScrollToBottom = false;

  readonly documentTypes = SupportTicketConstants.DOCUMENT_TYPES;
  editingAttachmentId: number | null = null;
  readonly todayStr = new Date().toISOString().split('T')[0];
  attachmentEditValues: { [id: number]: { dataEnrichmentDocumentType: string; dataEnrichmentDocumentDate: string } } = {};
  private attachmentOriginalValues: { [id: number]: { dataEnrichmentDocumentType: string; dataEnrichmentDocumentDate: string } } = {};

  readonly VALIDATION_LIMITS = {
    SUBJECT_MAX: 256,
    DESCRIPTION_MAX: 2020,
    COMMENT_MAX: 2000,
  };

  editForm: FormGroup;
  commentForm: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<TicketDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TicketDetailsDialogData,
    private actions$: Actions
  ) {
    this.editForm = this.fb.group({
      subject: [
        "",
        [
          Validators.required,
          Validators.maxLength(this.VALIDATION_LIMITS.SUBJECT_MAX),
        ],
      ],
      description: [
        "",
        [Validators.maxLength(this.VALIDATION_LIMITS.DESCRIPTION_MAX)],
      ],
      ticketCategoryId: [{ value: "", disabled: true }, [Validators.required]],
      ticketStatusId: ["", [Validators.required]],
    });

    this.commentForm = this.fb.group({
      comment: ["", [Validators.maxLength(this.VALIDATION_LIMITS.COMMENT_MAX)]],
    });
  }

  ngOnInit(): void {
    this.store.dispatch(IssueTrackerActions.getTicketCategories());
    this.store.dispatch(IssueTrackerActions.getTicketStatuses());

    this.activeUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.userRoles = user.roles || [];
        this.updateFormPermissions();
      }
    });

    this.store
      .select(selectTicketCategories)
      .pipe(takeUntil(this.destroy$))
      .subscribe((categories) => {
        this.categories = categories.filter((c) => c.id != 1001);
      });

    this.store
      .select(selectTicketStatuses)
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => {
        this.statuses = statuses;
      });

    this.store
      .select(selectTickets)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tickets) => {
        const updatedTicket = tickets.find((t) => t.id === this.data.ticketId);
        if (updatedTicket) {
          this.ticketSubject.next(updatedTicket);
        }
      });

    this.store
      .select(selectOrgs)
      .pipe(takeUntil(this.destroy$))
      .subscribe((orgs) => {
        this.organizations = orgs || [];
      });

    this.store
      .select(selectImpactedMeasureOptions)
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.impactedMeasureOptions = options;
      });

    this.actions$
      .pipe(
        ofType(IssueTrackerActions.updateTicketSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isUpdating = false;
        this.dialogRef.close({ updated: true });
      });

    this.actions$
      .pipe(
        ofType(IssueTrackerActions.updateTicketFailure),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        this.isUpdating = false;
        console.error("Update failed:", action.error);
      });

    this.actions$
      .pipe(
        ofType(IssueTrackerActions.uploadTicketAttachmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.snackBar.open("Attachment uploaded successfully.", "Close", { duration: 4000 });
      });

    this.actions$
      .pipe(
        ofType(IssueTrackerActions.uploadTicketAttachmentFailure),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        this.errorDialogService.showError(
          action.error || "An error occurred while uploading the attachment. Please try again.",
          "Upload Failed"
        );
      });

    this.initializeEditForm();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.ticketSubject.complete();
  }

  isSuperUser(): boolean {
    return ROLE_GROUPS.SUPER_USER.some(role => this.userRoles.includes(role));
  }

  private updateFormPermissions(): void {
    const statusControl = this.editForm.get("ticketStatusId");
    if (statusControl) {
      if (this.isSuperUser()) {
        statusControl.enable();
      } else {
        statusControl.disable();
      }
    }
  }

  private initializeEditForm(): void {
    const ticket = this.data.ticketData;
    this.editForm.patchValue({
      subject: ticket.subject,
      description: ticket.description,
      ticketCategoryId: ticket.ticketCategoryId,
      ticketStatusId: ticket.ticketStatusId,
    });
  }

  getSelectedCategoryName(): string {
    const categoryId = this.editForm.get("ticketCategoryId")?.value;
    const category = this.categories.find((c) => c.id === categoryId);
    return category?.name || "N/A";
  }

  getSelectedStatusName(): string {
    const statusId = this.editForm.get("ticketStatusId")?.value;
    const status = this.statuses.find((s) => s.id === statusId);
    return status?.name || "N/A";
  }

  get isCommentFormInvalid(): boolean {
    const commentValue = this.commentForm.get("comment")?.value;
    return (
      !commentValue ||
      !commentValue.trim() ||
      this.isAddingComment ||
      this.commentForm.invalid
    );
  }

  updateTicket(): void {
    if (this.editForm.valid && !this.isUpdating) {
      this.isUpdating = true;

      const updateRequest = {
        ticketId: this.data.ticketId,
        ...this.editForm.getRawValue(),
      };

      this.store.dispatch(
        IssueTrackerActions.updateTicket({
          ticketId: this.data.ticketId,
          updates: updateRequest,
        })
      );
    }
  }

  addComment(): void {
    const commentText = this.commentForm.value.comment?.trim();

    if (commentText && !this.isAddingComment && this.commentForm.valid) {
      this.isAddingComment = true;

      this.store.dispatch(
        IssueTrackerActions.addTicketComment({
          ticketId: this.data.ticketId,
          comment: commentText,
        })
      );

      this.commentForm.reset();
      this.shouldScrollToBottom = true;

      setTimeout(() => {
        this.isAddingComment = false;
      }, 500);
    }
  }

  deleteComment(commentId: number): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: "Delete Attachment",
        content: `Are you sure you want to delete this comment? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(
          IssueTrackerActions.deleteTicketComment({
            commentId: commentId,
          })
        );
      }
    });
  }

  deleteAttachment(attachmentId: number, fileName: string): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: "Delete Attachment",
        content: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(
          IssueTrackerActions.deleteTicketAttachment({
            attachmentId: attachmentId,
          })
        );
      }
    });
  }

  downloadAttachment(attachmentId: number, fileName: string): void {
    this.store.dispatch(
      IssueTrackerActions.downloadAttachment({
        attachmentId,
        fileName,
      })
    );
  }

  previewAttachment(attachmentId: number, fileName: string): void {
    this.store.dispatch(
      IssueTrackerActions.previewAttachment({
        attachmentId,
        fileName,
      })
    );
  }

  openAddAttachmentModal(): void {
    const dialogRef = this.dialog.open(AddTicketAttachmentModalComponent, {
      width: "500px",
      disableClose: true,
      data: {
        ticketId: this.data.ticketId,
      },
    });

    dialogRef.afterClosed().subscribe((result: TicketAttachmentResult) => {
      if (result) {
        this.store.dispatch(
          IssueTrackerActions.uploadTicketAttachment({
            ticketId: this.data.ticketId,
            file: result.file,
            isPhi: result.isPhi,
            dataEnrichmentDocumentType: result.dataEnrichmentDocumentType,
            dataEnrichmentDocumentDate: result.dataEnrichmentDocumentDate,
          })
        );
      }
    });
  }

  toggleComments(): void {
    this.showComments = !this.showComments;
    if (this.showComments) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.commentsContainer) {
        this.commentsContainer.nativeElement.scrollTop =
          this.commentsContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error("Error scrolling to bottom:", err);
    }
  }

  isPreviewable(mediaType?: string): boolean {
    if (!mediaType) return false;
    return SupportTicketConstants.PREVIEWABLE_MEDIA_TYPES.has(mediaType);
  }

  startEditAttachment(attachment: { id: number; documentType?: string; documentDate?: string }): void {
    this.editingAttachmentId = attachment.id;
    const original = {
      dataEnrichmentDocumentType: attachment.documentType || '',
      dataEnrichmentDocumentDate: (attachment.documentDate || '').split('T')[0],
    };
    this.attachmentOriginalValues[attachment.id] = original;
    this.attachmentEditValues[attachment.id] = { ...original };
  }

  hasAttachmentChanged(attachmentId: number): boolean {
    const current = this.attachmentEditValues[attachmentId];
    const original = this.attachmentOriginalValues[attachmentId];
    if (!current || !original) return false;
    return current.dataEnrichmentDocumentType !== original.dataEnrichmentDocumentType ||
           current.dataEnrichmentDocumentDate !== original.dataEnrichmentDocumentDate;
  }

  cancelEditAttachment(): void {
    this.editingAttachmentId = null;
  }

  saveAttachmentMetadata(attachmentId: number): void {
    const values = this.attachmentEditValues[attachmentId];
    if (!values?.dataEnrichmentDocumentType || !values?.dataEnrichmentDocumentDate) return;

    this.store.dispatch(
      IssueTrackerActions.updateTicketAttachment({
        attachmentId,
        dataEnrichmentDocumentType: values.dataEnrichmentDocumentType,
        dataEnrichmentDocumentDate: values.dataEnrichmentDocumentDate,
      })
    );
    this.editingAttachmentId = null;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return "";
    switch (status?.toLowerCase()) {
      case "new":
        return "status-new";
      case "in progress":
      case "in-progress":
        return "status-in-progress";
      case "closed":
        return "status-closed";
      case "resolved":
        return "status-closed";
      case "pending":
        return "status-pending";
      default:
        return "";
    }
  }

  getMissingMeasureTitles(measureCodes: string[] | undefined): string {
    if (!measureCodes || measureCodes.length === 0) return "";
    return measureCodes
      .map((code) => {
        const option = this.impactedMeasureOptions.find((candidate) => candidate.code === code);
        return option?.title || code;
      })
      .join(", ");
  }

  getOrganizationDisplayValue(organizationId: number): string {
    if (!this.organizations || this.organizations.length === 0) {
      return `Org ${organizationId}`;
    }

    const org = this.organizations.find(
      (o) => Number(o.id) === Number(organizationId)
    );
    if (org?.name && org?.legacyOrganizationID) {
      return `${org.name} - ${org.legacyOrganizationID}`;
    }
    return org?.name || `Org ${organizationId}`;
  }

  isTicketClosed(status: string | undefined): boolean {
    if (!status) return false;
    return (
      status.toLowerCase() === "closed" || status.toLowerCase() === "resolved"
    );
  }

  formatDate(date: string | Date): string {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  trackByCommentId(index: number, comment: TicketComment): number {
    return comment.id;
  }

  getCharacterCount(fieldName: string): number {
    const value = this.editForm.get(fieldName)?.value || "";
    return value.length;
  }

  getCommentCharacterCount(): number {
    const value = this.commentForm.get("comment")?.value || "";
    return value.length;
  }

  getRemainingCharacters(fieldName: string, maxLength: number): number {
    const currentLength = this.getCharacterCount(fieldName);
    return maxLength - currentLength;
  }

  getCommentRemainingCharacters(): number {
    const currentLength = this.getCommentCharacterCount();
    return this.VALIDATION_LIMITS.COMMENT_MAX - currentLength;
  }
}
